import SeoPage from "./seo";
import TodayBefore from "./today-before";
import TrendScout from "./trendscout";
import ClipToArticle from "./clip-to-article";
import Configurations from "./configurations";
import Content from "./content";
import Editor from "./editor";
import Highlights from "./highlights";
import Home from "./home";
import Player from "./player";
import ReframeEditor from "./reframe-editor";
import OverlayTemplates from "./overlay-templates";
import Queue from "./queue";
import Recordings from "./recordings";
import Stream from "./stream";
import Users from "./users";
import Statistics from "./statistics";
import Settings from "./settings";
// Array order IS the sidebar order (entries with a `parent` never render there).
// Flow: hub → content sources → production → analysis → ops, with the
// admin-only pages last so a non-admin sidebar ends cleanly at Queue Manager.
const routes = [
  {
    name: "home",
    route: "/",
    icon: "home",
    component: Home,
    tags: ["home", "main", "landing"],
  },
  {
    name: "content",
    route: "/content",
    icon: "content",
    component: Content,
    tags: ["home", "main", "landing"],
  },
  {
    name: "trendscout",
    route: "/trendscout",
    icon: "trendscout",
    component: TrendScout,
    // Feed + article generation is open to every role, matching the API
    // (trendscout.routes.ts READ). The settings tab and the scan trigger stay
    // admin-only — gated inside the page.
    requiredRoles: ["admin", "user"],
    tags: ["trends", "news", "scout", "google news", "rss"],
  },
  {
    name: "todaybefore",
    route: "/today-before",
    icon: "todaybefore",
    component: TodayBefore,
    // Feed + generation open to every role, matching the API
    // (todaybefore.routes.ts READ). Refresh is admin-only, gated in the page.
    requiredRoles: ["admin", "user"],
    tags: ["history", "on this day", "wikipedia", "knesset", "היום לפני"],
  },
  {
    name: "highlights",
    route: "/highlights",
    icon: "highlights",
    component: Highlights,
    // Editorial triage page — open to the Reshet editors and staff
    // (user), matching the API (highlights.routes.ts requireAnyRole), not admin-only.
    requiredRoles: ["admin", "user"],
    tags: ["highlights", "candidates", "editor", "tv", "reshet"],
  },
  {
    name: "clipToArticle",
    route: "/clip-to-article",
    icon: "editor",
    component: ClipToArticle,
    // Self-service escape hatch for when the highlighter missed a story or cut
    // it wrong — same audience as Highlights, matching the API (clips.routes.ts
    // is auth-only, unlike the admin-only /recordings it reads from).
    requiredRoles: ["admin", "user"],
    tags: ["clip", "recording", "video2article", "shows", "tv", "manual"],
  },
  {
    name: "stream",
    route: "/stream",
    icon: "stream",
    component: Stream,
    tags: ["home", "main", "landing"],
  },
  {
    name: "overlayTemplates",
    route: "/overlay-templates",
    icon: "editor",
    component: OverlayTemplates,
    tags: ["overlay", "templates", "reframer"],
  },
  {
    name: "seo",
    route: "/seo",
    icon: "seo",
    component: SeoPage,
    tags: ["seo", "geo", "analysis", "ranklens"],
  },
  {
    name: "queue",
    route: "/queue",
    icon: "queue",
    component: Queue,
    tags: ["home", "main", "landing"],
  },
  // --- admin-only, keep last ---
  {
    name: "recordings",
    route: "/recordings",
    icon: "stream",
    component: Recordings,
    requiredRole: "admin",
    tags: ["recordings", "admin", "highlighter", "rerun", "debug"],
  },
  {
    name: "statistics",
    route: "/statistics",
    icon: "statistics",
    component: Statistics,
    requiredRole: "admin",
    tags: ["statistics", "admin", "analytics", "reports"],
  },
  {
    name: "users",
    route: "/users",
    icon: "users",
    component: Users,
    requiredRole: "admin",
    tags: ["users", "admin", "roles", "access"],
  },
  {
    name: "settings",
    route: "/settings",
    icon: "settings",
    component: Settings,
    requiredRole: "admin",
    tags: ["settings", "admin", "recording", "padding"],
  },
  // --- not in the sidebar: child routes (have a `parent`) ---
  {
    name: "editor",
    route: "/editor",
    icon: "editor",
    parent: "/content",
    component: Editor,
    tags: ["home", "main", "landing"],
  },
  {
    name: "player",
    route: "/player",
    icon: "player",
    parent: "/content",
    component: Player,
    tags: ["home", "main", "landing"],
  },
  {
    name: "reframe-editor",
    route: "/reframe-editor",
    icon: "reframe-editor",
    parent: "/content",
    component: ReframeEditor,
    tags: ["reframe", "editor", "video"],
  },
  {
    name: "configurations",
    route: "/site",
    icon: "sites",
    parent: "/sites",
    component: Configurations,
    tags: ["home", "main", "landing"],
  },
];

export default routes;

// A route is allowed when the user's role satisfies its requirement: a
// `requiredRoles` allow-list (any match), else a single exact `requiredRole`,
// else (no requirement) open to any authenticated user.
export const routeAllowed = (route, role) =>
  route?.requiredRoles
    ? route.requiredRoles.includes(role)
    : !route?.requiredRole || route.requiredRole === role;
